"use client";

import React, { useState, useMemo } from "react";
import Link from "next/link";
import { Search, ArrowRight } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { getSubcategoryIcon } from "@/lib/subcategoryIcons";

interface SubItem {
  name: string;
  slug: string;
  url: string;
  icon?: string;
  namesCount: number;
}

interface CatItem {
  name: string;
  slug: string;
  icon: string;
  url: string;
  description: string;
  subcategories: SubItem[];
}

const ALPHABET = "#ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("");

export default function DirectoryClient({
  categories,
}: {
  categories: CatItem[];
}) {
  const [search, setSearch] = useState("");
  const [selectedCat, setSelectedCat] = useState<string>("all");
  const [selectedLetter, setSelectedLetter] = useState<string>("all");

  // Flatten all subcategories with category metadata and distinct icons
  const allSubcategories = useMemo(() => {
    return categories.flatMap((cat) =>
      cat.subcategories.map((sub) => ({
        ...sub,
        categoryName: cat.name,
        categoryIcon: cat.icon,
        categorySlug: cat.slug,
        icon: sub.icon || getSubcategoryIcon(sub.name || sub.slug, cat.slug),
      }))
    );
  }, [categories]);

  // Filtered items
  const filtered = useMemo(() => {
    let list = allSubcategories;

    // Filter by Category
    if (selectedCat !== "all") {
      list = list.filter((item) => item.categorySlug === selectedCat);
    }

    // Filter by Alphabet Letter
    if (selectedLetter !== "all") {
      if (selectedLetter === "#") {
        list = list.filter((item) => !/^[A-Za-z]/.test(item.name));
      } else {
        list = list.filter((item) =>
          item.name.toUpperCase().startsWith(selectedLetter)
        );
      }
    }

    // Filter by Search text
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(
        (item) =>
          item.name.toLowerCase().includes(q) ||
          item.categoryName.toLowerCase().includes(q)
      );
    }

    return list.sort((a, b) => a.name.localeCompare(b.name));
  }, [allSubcategories, search, selectedCat, selectedLetter]);

  // Group filtered items by first letter for directory layout
  const groupedByLetter = useMemo(() => {
    const map = new Map<string, typeof filtered>();

    for (const item of filtered) {
      const firstChar = item.name.charAt(0).toUpperCase();
      const key = /[A-Z]/.test(firstChar) ? firstChar : "#";
      if (!map.has(key)) {
        map.set(key, []);
      }
      map.get(key)!.push(item);
    }

    return Array.from(map.entries()).sort(([a], [b]) => a.localeCompare(b));
  }, [filtered]);

  return (
    <div className="space-y-8">
      {/* Controls: Search & Category Pills */}
      <div className="p-5 rounded-3xl border border-border/80 bg-card/85 backdrop-blur-xl shadow-lg space-y-4">
        {/* Search Input */}
        <div className="relative">
          <Search className="w-5 h-5 text-primary absolute left-4 top-1/2 -translate-y-1/2" />
          <Input
            type="text"
            placeholder="Search all generators by name or realm (e.g. Elf, Dragon, Tavern, French)..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-12 h-12 text-sm rounded-2xl bg-background/60"
          />
          {search && (
            <button
              onClick={() => setSearch("")}
              className="absolute right-4 top-1/2 -translate-y-1/2 text-xs font-semibold text-muted-foreground hover:text-foreground"
            >
              Clear
            </button>
          )}
        </div>

        {/* Category Filters */}
        <div className="flex flex-wrap items-center gap-2">
          <Button
            variant={selectedCat === "all" ? "fantasy" : "outline"}
            size="sm"
            onClick={() => setSelectedCat("all")}
            className="rounded-xl text-xs h-8"
          >
            All Realms
          </Button>

          {categories.map((cat) => (
            <Button
              key={cat.slug}
              variant={selectedCat === cat.slug ? "fantasy" : "outline"}
              size="sm"
              onClick={() => setSelectedCat(selectedCat === cat.slug ? "all" : cat.slug)}
              className="rounded-xl text-xs h-8 gap-1.5"
            >
              <span>{cat.icon}</span>
              <span>{cat.name.replace(" Names", "")}</span>
            </Button>
          ))}
        </div>

        {/* Alphabet A-Z Jump Bar */}
        <div className="flex flex-wrap items-center justify-center gap-1 pt-2 border-t border-border/40">
          <button
            onClick={() => setSelectedLetter("all")}
            className={`h-7 px-2.5 rounded-lg text-xs font-semibold transition-colors ${
              selectedLetter === "all"
                ? "bg-primary text-primary-foreground"
                : "text-muted-foreground hover:text-foreground hover:bg-muted"
            }`}
          >
            ALL
          </button>
          {ALPHABET.map((letter) => (
            <button
              key={letter}
              onClick={() => setSelectedLetter(selectedLetter === letter ? "all" : letter)}
              className={`h-7 w-7 rounded-lg text-xs font-semibold transition-colors ${
                selectedLetter === letter
                  ? "bg-primary text-primary-foreground"
                  : "text-muted-foreground hover:text-foreground hover:bg-muted"
              }`}
            >
              {letter}
            </button>
          ))}
        </div>
      </div>

      {/* Result Count Banner */}
      <div className="flex items-center justify-between px-2 text-xs text-muted-foreground">
        <span>
          Showing <strong className="text-foreground">{filtered.length}</strong> naming realms
          {selectedCat !== "all" && ` in ${categories.find((c) => c.slug === selectedCat)?.name}`}
          {selectedLetter !== "all" && ` starting with "${selectedLetter}"`}
        </span>
        {(search || selectedCat !== "all" || selectedLetter !== "all") && (
          <button
            onClick={() => {
              setSearch("");
              setSelectedCat("all");
              setSelectedLetter("all");
            }}
            className="text-primary hover:underline font-semibold"
          >
            Reset Filters
          </button>
        )}
      </div>

      {/* Grouped Alphabetical Listings */}
      {groupedByLetter.length > 0 ? (
        <div className="space-y-12">
          {groupedByLetter.map(([letter, items]) => (
            <div key={letter} className="space-y-4">
              {/* Letter Header */}
              <div className="flex items-center gap-3 pb-2 border-b border-border/80">
                <span className="h-10 w-10 rounded-2xl bg-primary/10 text-primary border border-primary/30 flex items-center justify-center font-fantasy font-bold text-lg">
                  {letter}
                </span>
                <span className="text-xs font-semibold text-muted-foreground">
                  {items.length} realm{items.length === 1 ? "" : "s"}
                </span>
              </div>

              {/* Items Grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2.5">
                {items.map((item) => (
                  <Link
                    key={`${item.categorySlug}-${item.slug}`}
                    href={item.url}
                    className="group p-3 rounded-2xl border border-border/60 bg-card/60 hover:bg-primary/5 hover:border-primary/40 transition-all duration-200 flex items-center justify-between shadow-xs"
                  >
                    <div className="flex items-center gap-2.5 min-w-0 pr-2">
                      <span className="text-lg shrink-0 group-hover:scale-110 transition-transform">
                        {item.icon}
                      </span>
                      <div className="min-w-0">
                        <p className="text-xs font-semibold text-foreground group-hover:text-primary transition-colors truncate">
                          {item.name}
                        </p>
                        <p className="text-[10px] text-muted-foreground truncate">
                          {item.categoryName.replace(" Names", "")}
                        </p>
                      </div>
                    </div>

                    <ArrowRight className="w-3.5 h-3.5 text-muted-foreground/40 group-hover:text-primary group-hover:translate-x-0.5 transition-all shrink-0" />
                  </Link>
                ))}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="py-20 text-center rounded-3xl border border-border/60 bg-card/40">
          <p className="text-base text-muted-foreground mb-4">
            No name generators matched your criteria.
          </p>
          <Button
            variant="outline"
            onClick={() => {
              setSearch("");
              setSelectedCat("all");
              setSelectedLetter("all");
            }}
            className="rounded-xl"
          >
            Clear All Filters
          </Button>
        </div>
      )}
    </div>
  );
}
